import React from "react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { Check, Lock } from "lucide-react";
import { ChangeUsersPasswordAction } from "store/middlewares/user";
import { closeAdminModal } from "store/slices/common/adminModal.slice";
import { Button, PasswordInput } from "components/ui";
import { SubmitLoaderButton } from "components/atoms";

// Password rules: shown as the checklist AND enforced on submit.
// Delete a line to relax a rule (match what your backend really requires).
const RULES = [
  { label: "8 or more characters", test: (v) => v.length >= 8 },
  {
    label: "Upper and lower case",
    test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v),
  },
  { label: "At least one number", test: (v) => /\d/.test(v) },
  { label: "One special character", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

// Body sent to PUT /admin/user/:id/password  (the id is taken out by the service).
// Change the key names here if your API expects different ones.
const buildPayload = (values) => ({
  password: values.password,
  // confirmPassword: values.confirmPassword,
});

const ModalChangePassword = ({ row }) => {
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("New password is required")
        .test("rules", "Password does not meet all the requirements", (v) =>
          RULES.every((r) => r.test(v || "")),
        ),
      confirmPassword: Yup.string()
        .required("Please confirm the password")
        .oneOf([Yup.ref("password")], "Passwords do not match"),
    }),
    onSubmit: (values) => {
      dispatch(
        ChangeUsersPasswordAction({
          id: row.id,
          ...buildPayload(values),
          onSuccess: () => {
            formik.setSubmitting(false);
            dispatch(closeAdminModal());
          },
          onError: () => {
            formik.setSubmitting(false);
          },
        }),
      );
    },
  });

  const field = (name, label) => (
    <div className="space-y-2">
      <label htmlFor={name} className="text-sm font-semibold">
        {label}
      </label>
      <PasswordInput
        id={name}
        name={name}
        className="w-full"
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        isSpace
        error={formik.errors[name] && formik.touched[name]}
        errorMsg={formik.errors[name]}
        placeholder={label}
        variant="lg"
      />
    </div>
  );
  return (
    <form onSubmit={formik.handleSubmit} className="grid gap-5">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <Lock className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">Change password?</h2>
      </div>

      <p className="text-sm leading-6 text-secondary-foreground">
        Set a new password for this{" "}
        <span className="break-all text-sm font-medium text-foreground">
          {row?.email}
        </span>{" "}
        user. They will be signed out of every device.
      </p>

      {/* Whose password this is */}
      {/* <div className="flex items-center gap-2.5 rounded-lg bg-muted px-3 py-2.5">
        <Mail className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="break-all text-sm font-medium text-foreground">
          {row?.email}
        </span>
      </div> */}

      {field("password", "New password")}
      {field("confirmPassword", "Confirm password")}

      {/* Live checklist */}
      <ul className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
        {RULES.map((rule) => {
          const ok = rule.test(formik.values.password || "");
          return (
            <li
              key={rule.label}
              className="flex items-center gap-2 text-[13px] text-secondary-foreground"
            >
              <span
                className={
                  ok
                    ? "flex size-4 shrink-0 items-center justify-center rounded-full bg-green-700 text-white"
                    : "size-4 shrink-0 rounded-full border-[1.5px] border-muted-foreground/60"
                }
              >
                {ok && (
                  <Check
                    className="size-2.5"
                    strokeWidth={3.5}
                    aria-hidden="true"
                  />
                )}
              </span>
              {rule.label}
            </li>
          );
        })}
      </ul>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          btnText="Change password"
          type="submit"
          size="lg"
          isDisable={formik.isSubmitting}
        />
      </div>
    </form>
  );
};

export default ModalChangePassword;
